import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useServicesContext } from "../context/services-context";
import { useServicesUIStore } from "../store/use-services-ui-store";
import type { CategorySortUpdate, ServiceSortUpdate } from "../types";

export function useDragAndDrop() {
  const { setIsDragInProgress, setActiveDragId, setServiceDragInProgress, setActiveServiceDragId } =
    useServicesUIStore();

  const {
    currentSalon,
    filteredCategories,
    filteredServicesByCategory,
    updateCategorySortOrder,
    updateServiceSortOrder,
  } = useServicesContext();

  // Helper function to determine if an ID is a category
  const isCategoryId = (id: string) => {
    return filteredCategories.some((cat) => cat.id === id);
  };

  // Helper function to find service and its category
  const findServiceAndCategory = (serviceId: string) => {
    for (const category of filteredCategories) {
      const services = filteredServicesByCategory[category.id] || [];
      const service = services.find((s) => s.id === serviceId);
      if (service) {
        return { service, category, services };
      }
    }
    return null;
  };

  // Find active drag category for overlay
  const activeDragCategory = useServicesUIStore((state) =>
    state.activeDragId ? filteredCategories.find((cat) => cat.id === state.activeDragId) : null
  );

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id as string;
    const isDraggingCategory = isCategoryId(activeId);

    if (isDraggingCategory) {
      setIsDragInProgress(true);
      setActiveDragId(activeId);
    } else {
      setServiceDragInProgress(true);
      setActiveServiceDragId(activeId);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragInProgress(false);
    setActiveDragId(null);
    setServiceDragInProgress(false);
    setActiveServiceDragId(null);

    if (!over || active.id === over.id || !currentSalon?.id) {
      return;
    }

    const isDraggingCategory = isCategoryId(active.id as string);
    const isOverCategory = isCategoryId(over.id as string);

    if (isDraggingCategory && isOverCategory) {
      // Handle category reordering
      const oldIndex = filteredCategories.findIndex((cat) => cat.id === active.id);
      const newIndex = filteredCategories.findIndex((cat) => cat.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedCategories = arrayMove(filteredCategories, oldIndex, newIndex);

        const updates: CategorySortUpdate[] = reorderedCategories.map((category, index) => ({
          categoryId: category.id,
          sortOrder: index,
        }));

        await updateCategorySortOrder(updates);
      }
    } else if (!isDraggingCategory && !isOverCategory) {
      // Handle service reordering within the same category
      const activeServiceData = findServiceAndCategory(active.id as string);
      const overServiceData = findServiceAndCategory(over.id as string);

      if (
        activeServiceData &&
        overServiceData &&
        activeServiceData.category.id === overServiceData.category.id
      ) {
        // Services are in the same category, allow reordering
        const services = activeServiceData.services;
        const oldIndex = services.findIndex((s) => s.id === active.id);
        const newIndex = services.findIndex((s) => s.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedServices = arrayMove(services, oldIndex, newIndex);

          const updates: ServiceSortUpdate[] = reorderedServices.map((service, index) => ({
            serviceId: service.id,
            sortOrder: index,
          }));

          await updateServiceSortOrder(updates);
        }
      }
      // If services are in different categories, do nothing (constraint)
    }
  };

  return {
    handleDragStart,
    handleDragEnd,
    activeDragCategory,
    isCategoryId,
    findServiceAndCategory,
  };
}
